import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Phone, Mail, MapPin, Users, Car, Package,
  Send, ClipboardList, ShieldAlert, RotateCcw, Building2,
  FileText, ReceiptText, IndianRupee,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { k: "overview", label: "Overview" },
  { k: "users", label: "Users" },
  { k: "job_cards", label: "Job Cards" },
  { k: "estimates", label: "Estimates" },
  { k: "invoices", label: "Invoices" },
  { k: "enquiries", label: "Parts Enquiries" },
];

const inr = (v) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(v || 0));

const StatBox = ({ label, value, Icon }) => (
  <div className="bg-white rounded-lg border border-[#E4E7EC] p-4">
    <div className="flex items-center gap-2 text-[#457b9d] mb-2">
      <Icon size={14} /> <span className="text-[11px] uppercase tracking-wider font-semibold">{label}</span>
    </div>
    <div className="text-[22px] font-bold text-[#1d3557] font-mono">{value}</div>
  </div>
);

export default function AdminGarageProfile() {
  const { gid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [suspendReason, setSuspendReason] = useState("");
  const [suspendOpen, setSuspendOpen] = useState(false);

  // Cross-garage ops data (loaded lazily per tab)
  const [jcRows, setJcRows] = useState(null);
  const [estRows, setEstRows] = useState(null);
  const [invRows, setInvRows] = useState(null);
  const [enqRows, setEnqRows] = useState(null);
  const [commSummary, setCommSummary] = useState(null); // For Overview: this-week + hv counts

  const load = () => {
    api.get(`/admin/garages/${gid}`).then((r) => setData(r.data)).catch((e) => {
      toast.error(formatApiError(e));
    });
  };
  useEffect(() => { load();   }, [gid]);

  // Load commercial summary for Overview
  useEffect(() => {
    if (!gid) return;
    Promise.all([
      api.get("/admin/ops/job-cards", { params: { garage_id: gid, sort: "value_desc", limit: 500 } }).then((r) => r.data.rows || []).catch(() => []),
      api.get("/admin/ops/estimates", { params: { garage_id: gid, sort: "value_desc", limit: 500 } }).then((r) => r.data.rows || []).catch(() => []),
      api.get("/admin/ops/invoices", { params: { garage_id: gid, limit: 500 } }).then((r) => r.data.rows || []).catch(() => []),
      api.get(`/admin/enquiries`, { params: { garage_id: gid, limit: 500 } }).then((r) => r.data || []).catch(() => []),
    ]).then(([jc, est, inv, enq]) => {
      const weekAgo = Date.now() - 7 * 24 * 3600 * 1000;
      const isRecent = (d) => d && new Date(d).getTime() >= weekAgo;
      setCommSummary({
        jc_week: jc.filter((r) => isRecent(r.created_at)).length,
        est_week: est.filter((r) => isRecent(r.created_at)).length,
        inv_week: inv.filter((r) => isRecent(r.issued_at || r.created_at)).length,
        enq_week: enq.filter((r) => isRecent(r.created_at)).length,
        hv_jc: jc.filter((r) => r.high_value).length,
        hv_est: est.filter((r) => r.high_value).length,
      });
      setJcRows(jc);
      setEstRows(est);
      setInvRows(inv);
      setEnqRows(enq);
    });
  }, [gid]);

  const canSuspend = ["platform_owner", "platform_admin"].includes(user?.admin_role);

  const suspend = async () => {
    if (!suspendReason.trim()) {
      toast.error("Reason is required");
      return;
    }
    try {
      await api.post(`/admin/garages/${gid}/suspend`, { reason: suspendReason });
      toast.success("Garage suspended");
      setSuspendOpen(false);
      setSuspendReason("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reactivate = async () => {
    if (!window.confirm("Reactivate this garage?")) return;
    try {
      await api.post(`/admin/garages/${gid}/reactivate`);
      toast.success("Garage reactivated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <div className="p-6 text-[13px] text-[#667085]">Loading garage…</div>;

  const g = data.garage;
  const s = data.stats || {};
  const suspended = !!g.suspended;

  return (
    <div className="space-y-5" data-testid="admin-garage-profile">
      <button
        onClick={() => nav("/admin/garages")}
        data-testid="admin-garage-back"
        className="text-[12px] text-[#457b9d] hover:text-[#e63946] font-semibold inline-flex items-center gap-1"
      >
        <ArrowLeft size={13} /> Back to garages
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-[#E4E7EC] p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Garage</div>
            <h1 className="text-[26px] font-bold text-[#1d3557] leading-tight flex items-center gap-2" data-testid="admin-garage-name">
              <Building2 size={20} className="text-[#457b9d]" />
              {g.name}
              {suspended && (
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#e63946]/10 text-[#e63946]">
                  Suspended
                </span>
              )}
            </h1>
            <div className="text-[11px] text-[#667085] font-mono mt-1">Garage ID: {g.id}</div>
            <div className="mt-3 flex items-center gap-3 flex-wrap text-[12px] text-[#667085]">
              {g.phone && <span className="inline-flex items-center gap-1"><Phone size={12} /> {g.phone}</span>}
              {g.email && <span className="inline-flex items-center gap-1"><Mail size={12} /> {g.email}</span>}
              {(g.address || g.city) && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {g.address || g.city}</span>}
            </div>
            {suspended && g.suspended_reason && (
              <div className="mt-3 text-[12px] text-[#e63946] bg-[#e63946]/5 border border-[#e63946]/20 rounded-md px-3 py-2">
                <strong>Suspension reason:</strong> {g.suspended_reason}
              </div>
            )}
          </div>
          {canSuspend && (
            <div className="flex items-center gap-2">
              {suspended ? (
                <Button
                  onClick={reactivate}
                  data-testid="admin-garage-reactivate"
                  className="h-9 bg-[#12B76A] hover:opacity-90 rounded-md text-white font-semibold text-[13px] px-3"
                >
                  <RotateCcw size={14} className="mr-1.5" /> Reactivate
                </Button>
              ) : (
                <Dialog open={suspendOpen} onOpenChange={setSuspendOpen}>
                  <DialogTrigger asChild>
                    <Button
                      data-testid="admin-garage-suspend-open"
                      className="h-9 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold text-[13px] px-3"
                    >
                      <ShieldAlert size={14} className="mr-1.5" /> Suspend garage
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-lg border-[#E4E7EC] max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-[#1d3557] font-semibold">Suspend {g.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                      <p className="text-[12px] text-[#667085]">
                        This will prevent all users from logging in until reactivated. A reason is required for the audit log.
                      </p>
                      <Textarea
                        rows={3}
                        data-testid="admin-garage-suspend-reason"
                        placeholder="Reason for suspension…"
                        value={suspendReason}
                        onChange={(e) => setSuspendReason(e.target.value)}
                        className="rounded-md"
                      />
                      <Button
                        onClick={suspend}
                        data-testid="admin-garage-suspend-confirm"
                        className="w-full h-10 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold"
                      >
                        Confirm suspension
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stat boxes */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <StatBox label="Users" value={s.users_count ?? data.users?.length ?? 0} Icon={Users} />
        <StatBox label="Customers" value={s.customers ?? 0} Icon={Users} />
        <StatBox label="Vehicles" value={s.vehicles ?? 0} Icon={Car} />
        <StatBox label="JC total" value={s.job_cards_total ?? 0} Icon={ClipboardList} />
        <StatBox label="JC / week" value={s.job_cards_this_week ?? 0} Icon={ClipboardList} />
        <StatBox label="Parts" value={s.parts ?? 0} Icon={Package} />
      </div>

      {/* Tabs */}
      <div className="border-b border-[#E4E7EC]">
        <div className="inline-flex gap-1">
          {TABS.map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`admin-garage-tab-${t.k}`}
              className={`h-10 px-4 text-[13px] font-semibold border-b-2 -mb-px transition-colors ${
                tab === t.k
                  ? "border-[#e63946] text-[#1d3557]"
                  : "border-transparent text-[#667085] hover:text-[#1d3557]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "overview" && (
        <div className="space-y-4" data-testid="admin-garage-overview">
          {/* This-week + High Value KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <StatBox label="JCs / week" value={commSummary?.jc_week ?? "—"} Icon={ClipboardList} />
            <StatBox label="Estimates / week" value={commSummary?.est_week ?? "—"} Icon={FileText} />
            <StatBox label="Invoices / week" value={commSummary?.inv_week ?? "—"} Icon={ReceiptText} />
            <StatBox label="Enquiries / week" value={commSummary?.enq_week ?? "—"} Icon={Send} />
            <StatBox label="HV JCs" value={commSummary?.hv_jc ?? "—"} Icon={IndianRupee} />
            <StatBox label="HV Estimates" value={commSummary?.hv_est ?? "—"} Icon={IndianRupee} />
          </div>
          <div className="bg-white rounded-lg border border-[#E4E7EC] p-5 space-y-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Registered</div>
              <div className="text-[13px] text-[#1d3557] font-mono">{fmtDate(g.created_at)}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Address</div>
              <div className="text-[13px] text-[#1d3557]">{g.address || "—"}</div>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Send size={14} className="text-[#457b9d]" />
              <Link to={`/admin/enquiries?garage=${gid}`} className="text-[13px] text-[#457b9d] hover:text-[#e63946] font-semibold">
                {s.enquiries} enquiry(ies) submitted →
              </Link>
            </div>
          </div>
        </div>
      )}

      {tab === "users" && (
        <div className="bg-white rounded-lg border border-[#E4E7EC] overflow-hidden" data-testid="admin-garage-users">
          <table className="w-full text-[13px]">
            <thead className="bg-[#F7F9FC] text-[11px] uppercase tracking-wider text-[#667085]">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Email</th>
                <th className="text-left px-4 py-3 font-semibold">Role</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1FAEE]">
              {(data.users || []).map((u) => (
                <tr key={u.id} data-testid={`admin-garage-user-${u.id}`}>
                  <td className="px-4 py-3 text-[#1d3557] font-semibold">{u.name}</td>
                  <td className="px-4 py-3 text-[#667085]">{u.email}</td>
                  <td className="px-4 py-3 text-[12px] uppercase tracking-wider text-[#457b9d] font-semibold">{u.role}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${u.active ? "bg-[#12B76A]/10 text-[#12B76A]" : "bg-[#e63946]/10 text-[#e63946]"}`}>
                      {u.active ? "Active" : "Suspended"}
                    </span>
                  </td>
                </tr>
              ))}
              {(!data.users || data.users.length === 0) && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-[#667085]">No users yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "job_cards" && (
        <RowList
          rows={jcRows}
          testId="admin-garage-jcs"
          columns={["JC #", "Customer", "Vehicle", "Value", "Status", "Created"]}
          renderRow={(r) => ({
            to: `/admin/job-cards/${r.id}`,
            cells: [
              <span key="n" className="font-mono font-semibold">{r.number}</span>,
              r.customer_name || "—",
              <span key="v" className="font-mono">{r.vehicle_reg || "—"}</span>,
              <span key="val" className="font-mono font-semibold">{inr(r.value)}{r.high_value && <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded border text-[9px] font-bold uppercase tracking-wider bg-[#e63946]/8 text-[#e63946] border-[#e63946]/30">HV</span>}</span>,
              <span key="s" className="uppercase text-[10px] tracking-wider text-[#457b9d] font-semibold">{(r.status || "").replace(/_/g, " ")}</span>,
              fmtDate(r.created_at),
            ],
          })}
        />
      )}

      {tab === "estimates" && (
        <RowList
          rows={estRows}
          testId="admin-garage-estimates"
          columns={["EST #", "Customer", "Vehicle", "Total", "Status", "Created"]}
          renderRow={(r) => ({
            to: `/admin/estimates/${r.id}`,
            cells: [
              <span key="n" className="font-mono font-semibold">{r.number}</span>,
              r.customer_name || "—",
              <span key="v" className="font-mono">{r.vehicle_reg || "—"}</span>,
              <span key="val" className="font-mono font-semibold">{inr(r.total)}{r.high_value && <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded border text-[9px] font-bold uppercase tracking-wider bg-[#e63946]/8 text-[#e63946] border-[#e63946]/30">HV</span>}</span>,
              <span key="s" className="uppercase text-[10px] tracking-wider text-[#457b9d] font-semibold">{(r.status || "").replace(/_/g, " ")}</span>,
              fmtDate(r.created_at),
            ],
          })}
        />
      )}

      {tab === "invoices" && (
        <RowList
          rows={invRows}
          testId="admin-garage-invoices"
          columns={["INV #", "Customer", "Vehicle", "Billed", "Outstanding", "Payment"]}
          renderRow={(r) => ({
            to: `/admin/invoices/${r.id}`,
            cells: [
              <span key="n" className="font-mono font-semibold">{r.number}</span>,
              r.customer_name || "—",
              <span key="v" className="font-mono">{r.vehicle_reg || "—"}</span>,
              <span key="b" className="font-mono font-semibold">{inr(r.billed)}</span>,
              <span key="o" className={`font-mono ${r.outstanding > 0 ? "text-[#e63946] font-semibold" : "text-[#12B76A]"}`}>{inr(r.outstanding)}</span>,
              <span key="p" className="uppercase text-[10px] tracking-wider text-[#457b9d] font-semibold">{(r.payment_status || "").replace(/_/g, " ")}</span>,
            ],
          })}
        />
      )}

      {tab === "enquiries" && (
        <RowList
          rows={enqRows}
          testId="admin-garage-enquiries"
          columns={["Enquiry #", "Items", "Vehicle", "Status", "Sales Status", "Created"]}
          renderRow={(r) => ({
            to: `/admin/enquiries/${r.id}`,
            cells: [
              <span key="n" className="font-mono font-semibold">{r.number || r.id?.slice(0, 8)}</span>,
              String((r.items || []).length),
              <span key="v" className="font-mono">{r.vehicle?.reg_number || "—"}</span>,
              <span key="s" className="uppercase text-[10px] tracking-wider text-[#457b9d] font-semibold">{(r.status || "").replace(/_/g, " ")}</span>,
              <span key="ss" className="uppercase text-[10px] tracking-wider text-[#457b9d] font-semibold">{(r.sales_status || "—").replace(/_/g, " ")}</span>,
              fmtDate(r.created_at),
            ],
          })}
        />
      )}
    </div>
  );
}

const RowList = ({ rows, columns, renderRow, testId }) => {
  if (rows === null) return <div className="bg-white rounded-lg border border-[#E4E7EC] p-8 text-center text-[13px] text-[#667085]">Loading…</div>;
  if (rows.length === 0) return <div className="bg-white rounded-lg border border-[#E4E7EC] p-8 text-center text-[13px] text-[#667085]">No records for this garage.</div>;
  return (
    <div className="bg-white rounded-lg border border-[#E4E7EC] overflow-hidden" data-testid={testId}>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-[#F7F9FC] text-[10px] uppercase tracking-widest text-[#667085]">
            <tr>{columns.map((c) => <th key={c} className="text-left px-4 py-2 font-semibold">{c}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-[#F1FAEE]">
            {rows.slice(0, 100).map((r, i) => {
              const { to, cells } = renderRow(r);
              return (
                <tr key={r.id || i} className="hover:bg-[#F7F9FC] cursor-pointer" onClick={() => window.location.assign(to)}>
                  {cells.map((c, j) => <td key={j} className="px-4 py-2 text-[#1d3557]">{c}</td>)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
